import { useState, useEffect, useRef, useCallback } from 'react';
import { notificationApi } from '../api.js';
import type { Notification } from '../types.js';

/** All user-facing strings of the bell. Override any subset via the `labels` prop (e.g. for i18n). */
export interface NotificationBellLabels {
  bellTitle: string;
  dropdownTitle: string;
  markAllRead: string;
  empty: string;
  /** Relative-time strings. `{m}`/`{h}`/`{d}` are replaced with the elapsed count. */
  justNow: string;
  minutesAgo: string;
  hoursAgo: string;
  daysAgo: string;
}

export const defaultNotificationBellLabels: NotificationBellLabels = {
  bellTitle: 'Notifications',
  dropdownTitle: 'Notifications',
  markAllRead: 'Mark all read',
  empty: 'No notifications',
  justNow: 'just now',
  minutesAgo: '{m}m ago',
  hoursAgo: '{h}h ago',
  daysAgo: '{d}d ago',
};

interface NotificationBellProps {
  onNavigate: (link: string) => void;
  /** Override any user-facing string (e.g. for i18n). */
  labels?: Partial<NotificationBellLabels>;
}

export function NotificationBell({ onNavigate, labels }: NotificationBellProps) {
  const L: NotificationBellLabels = { ...defaultNotificationBellLabels, ...labels };
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const fetchUnreadCount = useCallback(async () => {
    try {
      const data = await notificationApi.getUnreadCount();
      setUnreadCount(data.count);
    } catch { /* ignore */ }
  }, []);

  const fetchNotifications = useCallback(async () => {
    try {
      const data = await notificationApi.getNotifications();
      setNotifications(data);
    } catch { /* ignore */ }
  }, []);

  // Poll unread count every 30s
  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, [fetchUnreadCount]);

  // Load full list when dropdown opens
  useEffect(() => {
    if (open) {
      fetchNotifications();
    }
  }, [open, fetchNotifications]);

  // Click outside to close
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const handleMarkAllRead = async () => {
    await notificationApi.markAllRead();
    setUnreadCount(0);
    setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
  };

  const handleClick = async (n: Notification) => {
    if (!n.is_read) {
      await notificationApi.markRead(n.id);
      setUnreadCount((c) => Math.max(0, c - 1));
      setNotifications((prev) =>
        prev.map((x) => (x.id === n.id ? { ...x, is_read: true } : x))
      );
    }
    setOpen(false);
    if (n.link) {
      onNavigate(n.link);
    }
  };

  const relativeTime = (dateStr: string) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return L.justNow;
    if (mins < 60) return L.minutesAgo.replace('{m}', String(mins));
    const hours = Math.floor(mins / 60);
    if (hours < 24) return L.hoursAgo.replace('{h}', String(hours));
    const days = Math.floor(hours / 24);
    return L.daysAgo.replace('{d}', String(days));
  };

  return (
    <div className="notification-bell" ref={ref}>
      <button
        className="notification-bell-btn"
        onClick={() => setOpen(!open)}
        title={L.bellTitle}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 0 1-3.46 0" />
        </svg>
        {unreadCount > 0 && (
          <span className="notification-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>
        )}
      </button>

      {open && (
        <div className="notification-dropdown">
          <div className="notification-dropdown-header">
            <span className="notification-dropdown-title">{L.dropdownTitle}</span>
            {unreadCount > 0 && (
              <button className="btn btn-ghost btn-xs" onClick={handleMarkAllRead}>
                {L.markAllRead}
              </button>
            )}
          </div>
          <div className="notification-dropdown-list">
            {notifications.length === 0 ? (
              <div className="notification-empty">{L.empty}</div>
            ) : (
              notifications.map((n) => (
                <div
                  key={n.id}
                  className={`notification-item ${!n.is_read ? 'notification-item--unread' : ''}`}
                  onClick={() => handleClick(n)}
                >
                  <div className="notification-item-title">{n.title}</div>
                  {n.body && <div className="notification-item-body">{n.body}</div>}
                  <div className="notification-item-time">{relativeTime(n.created_at)}</div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
